import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router'

import { ReportGenerationStage } from '@ankor-io/common/types/reports/generated-report'

import { PipelineSelectorView } from './pipeline-selector'
import { ReportGenerationJobsView } from './report-generation-jobs'
import { ReportGenerationRunPipelineView } from './report-generation-run-pipeline'
import { ReportGenerationRunStepsView } from './report-generation-run-steps'
import { ReportGenerationRunsView } from './report-generation-runs'

type ViewState =
  | { screen: 'selector' }
  | { screen: 'reportGenJobs' }
  | { screen: 'reportGenRuns'; jobId: string }
  | { screen: 'reportGenPipeline'; jobId: string; runId: string }
  | { screen: 'reportGenSteps'; jobId: string; runId: string; stageKey: ReportGenerationStage }

const REPORT_STAGE_VALUES = new Set<string>(Object.values(ReportGenerationStage))

const viewToParams = (view: ViewState): Record<string, string> => {
  switch (view.screen) {
    case 'selector':
      return {}
    case 'reportGenJobs':
      return { screen: 'reportGenJobs' }
    case 'reportGenRuns':
      return { screen: 'reportGenRuns', jobId: view.jobId }
    case 'reportGenPipeline':
      return { screen: 'reportGenPipeline', jobId: view.jobId, runId: view.runId }
    case 'reportGenSteps':
      return {
        screen: 'reportGenSteps',
        jobId: view.jobId,
        runId: view.runId,
        stageKey: view.stageKey,
      }
  }
}

const paramsToView = (params: URLSearchParams): ViewState => {
  const screen = params.get('screen')
  const jobId = params.get('jobId')
  const runId = params.get('runId')
  const stageKeyRaw = params.get('stageKey')

  if (screen === 'reportGenSteps' && jobId && runId && stageKeyRaw && REPORT_STAGE_VALUES.has(stageKeyRaw)) {
    return { screen: 'reportGenSteps', jobId, runId, stageKey: stageKeyRaw as ReportGenerationStage }
  }
  if (screen === 'reportGenPipeline' && jobId && runId) {
    return { screen: 'reportGenPipeline', jobId, runId }
  }
  if (screen === 'reportGenRuns' && jobId) {
    return { screen: 'reportGenRuns', jobId }
  }
  if (screen === 'reportGenJobs') {
    return { screen: 'reportGenJobs' }
  }
  return { screen: 'selector' }
}

export const OrganizationPipeline = ({ organizationId }: { organizationId: string }) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const view = useMemo(() => paramsToView(searchParams), [searchParams])
  const setView = useCallback(
    (next: ViewState) => setSearchParams(viewToParams(next), { replace: true }),
    [setSearchParams],
  )

  if (view.screen === 'reportGenSteps') {
    return (
      <ReportGenerationRunStepsView
        organizationId={organizationId}
        jobId={view.jobId}
        runId={view.runId}
        stageKey={view.stageKey}
        onBack={() => setView({ screen: 'reportGenPipeline', jobId: view.jobId, runId: view.runId })}
      />
    )
  }

  if (view.screen === 'reportGenPipeline') {
    return (
      <ReportGenerationRunPipelineView
        organizationId={organizationId}
        jobId={view.jobId}
        runId={view.runId}
        onBack={() => setView({ screen: 'reportGenRuns', jobId: view.jobId })}
        onSelectStage={(stageKey) =>
          setView({ screen: 'reportGenSteps', jobId: view.jobId, runId: view.runId, stageKey })
        }
      />
    )
  }

  if (view.screen === 'reportGenRuns') {
    return (
      <ReportGenerationRunsView
        organizationId={organizationId}
        jobId={view.jobId}
        onBack={() => setView({ screen: 'reportGenJobs' })}
        onSelectRun={(nextRunId) => setView({ screen: 'reportGenPipeline', jobId: view.jobId, runId: nextRunId })}
      />
    )
  }

  if (view.screen === 'reportGenJobs') {
    return (
      <ReportGenerationJobsView
        organizationId={organizationId}
        onBack={() => setView({ screen: 'selector' })}
        onSelectJob={(nextJobId) => setView({ screen: 'reportGenRuns', jobId: nextJobId })}
        onSelectLastRun={(nextJobId, nextRunId) =>
          setView({ screen: 'reportGenPipeline', jobId: nextJobId, runId: nextRunId })
        }
      />
    )
  }

  return <PipelineSelectorView organizationId={organizationId} />
}
